import { useState } from "react"

import { useTranslation } from "react-i18next"

import { ChevronDown, ChevronUp, Globe, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { logger } from "@/lib/logger"

type UrlListProps = {
  urls: string[]
  onRemove?: (index: number) => void
  maxInitialItems?: number
}

const MAX_INITIAL_RESULTS = 4

export function UrlList({ urls, onRemove, maxInitialItems = MAX_INITIAL_RESULTS }: UrlListProps) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  if (urls.length === 0) {
    return null
  }

  const visibleUrls = isExpanded ? urls : urls.slice(0, maxInitialItems)
  const hasMoreResults = urls.length > maxInitialItems

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        {visibleUrls.map((url, index) => (
          <UrlListItem
            key={`${url}-${index}`}
            url={url}
            onRemove={onRemove ? () => onRemove(index) : undefined}
          />
        ))}
      </div>

      {hasMoreResults && (
        <Button
          className="text-muted-foreground hover:text-foreground w-full text-xs"
          size="sm"
          variant="ghost"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <>
              <ChevronUp className="mr-1 size-3" />
              {t("pages.ai_explorer.show_less")}
            </>
          ) : (
            <>
              <ChevronDown className="mr-1 size-3" />
              {t("pages.ai_explorer.show_more", { count: urls.length - maxInitialItems })}
            </>
          )}
        </Button>
      )}
    </div>
  )
}

type UrlListItemProps = {
  url: string
  onRemove?: () => void
}

function UrlListItem({ url, onRemove }: UrlListItemProps) {
  let baseUrl = ""
  try {
    baseUrl = new URL(url).hostname
  } catch (error) {
    logger.error("Invalid URL:", error as Error)
    baseUrl = url
  }

  const iconSrc = `https://icons.duckduckgo.com/ip3/${baseUrl}.ico`

  return (
    <div className="hover:border-primary/30 flex items-center gap-2 rounded-md border px-2 py-1.5">
      <div className="bg-muted flex size-5 shrink-0 items-center justify-center rounded-full">
        <img
          alt={`${baseUrl} favicon`}
          className="size-3"
          src={iconSrc}
          onError={(e) => {
            e.currentTarget.style.display = "none"
            const sibling = e.currentTarget.nextElementSibling as HTMLElement
            sibling.classList.remove("hidden")
          }}
        />
        <Globe className="text-muted-foreground hidden size-3" />
      </div>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span className="text-foreground shrink-0 text-xs">{baseUrl}</span>
      </div>
      {onRemove && (
        <Button
          className="hover:bg-destructive/10 hover:text-destructive h-6 w-6 shrink-0 p-0"
          size="sm"
          variant="ghost"
          onClick={onRemove}
        >
          <X className="size-3" />
        </Button>
      )}
    </div>
  )
}
